/**
 * Year progress utilities - calculations and contextual messages
 */

/**
 * Check if a year is a leap year
 * @param {number} year
 * @returns {boolean}
 */
function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

/**
 * Get the number of days in a year
 * @param {number} year
 * @returns {number} 365 or 366
 */
function getDaysInYear(year) {
  return isLeapYear(year) ? 366 : 365;
}

/**
 * Get current day of year (1-based)
 * @param {Date} date
 * @returns {number}
 */
function getDayOfYear(date) {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start;
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
}

/**
 * Get ISO 8601 week number for a date
 * @param {Date} date
 * @returns {number} Week number (1-53)
 */
function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7; // Sunday = 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // Thursday in current week
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

/**
 * Get year progress data for a given year
 * @param {number} year - The year to calculate progress for
 * @returns {object} Progress data with percent, days elapsed/remaining, etc.
 */
export function getYearProgress(year) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const isCurrentYear = year === currentYear;
  const daysInYear = getDaysInYear(year);
  
  // For current year, calculate actual progress
  if (isCurrentYear) {
    const dayOfYear = getDayOfYear(now);
    const percent = Math.round((dayOfYear / daysInYear) * 100);
    const daysRemaining = daysInYear - dayOfYear;
    const remaining = 100 - percent;
    
    return {
      year,
      percent,
      remaining,
      dayOfYear,
      daysRemaining,
      daysInYear,
      isCurrentYear: true,
      isFutureYear: false
    };
  }
  
  // For past/future years, return static data
  const isFutureYear = year > currentYear;
  return {
    year,
    percent: isFutureYear ? 0 : 100,
    remaining: isFutureYear ? 100 : 0,
    dayOfYear: isFutureYear ? 0 : daysInYear,
    daysRemaining: isFutureYear ? daysInYear : 0,
    daysInYear,
    isCurrentYear: false,
    isFutureYear
  };
}

/**
 * Get a random item from an array
 * @param {Array} arr
 * @returns {*}
 */
function getRandomItem(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Interpolate variables in a message template
 * @param {string} template - Message with {variable} placeholders
 * @param {object} vars - Variables to interpolate
 * @returns {string} Interpolated message
 */
function interpolate(template, vars) {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return vars[key] !== undefined ? vars[key] : match;
  });
}

/**
 * Get the category key based on progress percentage
 * @param {number} percent - Progress percentage
 * @returns {string} Category key for translation lookup
 */
function getProgressCategory(percent) {
  if (percent < 5) return 'early';
  if (percent < 15) return 'building';
  if (percent < 25) return 'q1';
  if (percent < 40) return 'spring';
  if (percent < 50) return 'approaching';
  if (percent === 50) return 'halftime';
  if (percent < 65) return 'postHalf';
  if (percent < 75) return 'q3';
  if (percent < 85) return 'q4';
  if (percent < 95) return 'yearEnd';
  if (percent < 99) return 'final';
  return 'lastDays';
}

/**
 * Get a contextual message based on year progress
 * @param {object} progress - Progress data from getYearProgress()
 * @param {Function} t - Translation function from useTranslation
 * @returns {string} Interpolated message with variables replaced
 */
export function getProgressMessage(progress, t) {
  const { percent } = progress;
  const category = getProgressCategory(percent);
  
  // Get translated messages array for this category
  const messages = t(`yearProgress.messages.${category}`);
  
  // If no translation function or messages not found, return a basic message
  if (!messages || !Array.isArray(messages)) {
    return `${percent}% of ${progress.year} complete`;
  }
  
  // Get random message from the translated array
  const template = getRandomItem(messages);
  
  // Calculate ISO 8601 week number
  const now = new Date();
  const week = getISOWeek(now);
  
  // Interpolate variables
  return interpolate(template, {
    year: progress.year,
    percent: progress.percent,
    remaining: progress.remaining,
    day: progress.dayOfYear,
    week: week,
    daysRemaining: progress.daysRemaining,
    daysInYear: progress.daysInYear
  });
}
